"use client";

import { useRef, useCallback, useState } from "react";
import QRCode from "react-qr-code";
import { Heart, Copy, Check, Download } from "lucide-react";
import { Button } from "@/components/ui/button";
import { getTemplateConfig } from "@/lib/templates";
import { cn } from "@/lib/utils";

// ─── Constantes ───────────────────────────────────────────────────────────────

// Lucide Heart path (viewBox 0 0 24 24, closed path → preenchido com fill)
const HEART_PATH =
  "M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z";

// Tamanho do canvas de download
const CANVAS_SIZE = 512;
// Tamanho do coração no canvas (px)
const HEART_PX = 80;
// Raio do círculo branco por trás do coração no canvas
const CIRCLE_R = Math.round(HEART_PX * 0.54);

// ─── Helpers ──────────────────────────────────────────────────────────────────

/** Gera alturas de barras determinísticas a partir da URL. */
function generateBars(seed: string, count = 38): number[] {
  return Array.from({ length: count }, (_, i) => {
    let h = 0;
    for (let j = 0; j < seed.length; j++) {
      h = (Math.imul(31, h) + seed.charCodeAt(j)) | 0;
    }
    h = (Math.imul(h + i, 2654435769) | 0) >>> 0;
    return 15 + (h % 76);
  });
}

/** Carrega uma URL como Image para uso em canvas. */
function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
}

/**
 * Converte o SVG do QR Code para PNG, incluindo o coração vermelho centralizado.
 * Fluxo: serializa SVG → canvas → círculo branco + coração SVG → download.
 */
async function downloadQrAsPng(
  container: HTMLDivElement,
  filename: string,
  heartColor: string
): Promise<void> {
  const svg = container.querySelector("svg");
  if (!svg) return;

  const cx = CANVAS_SIZE / 2;
  const cy = CANVAS_SIZE / 2;

  // ── 1. Serializa o SVG do QR ───────────────────────────────────────────────
  const clone = svg.cloneNode(true) as SVGSVGElement;
  clone.setAttribute("xmlns", "http://www.w3.org/2000/svg");
  clone.setAttribute("width", String(CANVAS_SIZE));
  clone.setAttribute("height", String(CANVAS_SIZE));
  const qrBlob = new Blob(
    [new XMLSerializer().serializeToString(clone)],
    { type: "image/svg+xml;charset=utf-8" }
  );
  const qrUrl = URL.createObjectURL(qrBlob);

  // ── 2. Prepara o SVG do coração (mesmo path do Lucide, fill sólido) ────────
  const heartSvg = [
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"`,
    ` width="${HEART_PX}" height="${HEART_PX}">`,
    `<path fill="${heartColor}" stroke="none" d="${HEART_PATH}"/>`,
    `</svg>`,
  ].join("");
  const heartBlob = new Blob([heartSvg], { type: "image/svg+xml" });
  const heartUrl = URL.createObjectURL(heartBlob);

  try {
    const [qrImg, heartImg] = await Promise.all([
      loadImage(qrUrl),
      loadImage(heartUrl),
    ]);

    URL.revokeObjectURL(qrUrl);
    URL.revokeObjectURL(heartUrl);

    const canvas = document.createElement("canvas");
    canvas.width = CANVAS_SIZE;
    canvas.height = CANVAS_SIZE;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    // a. Fundo branco
    ctx.fillStyle = "#FFFFFF";
    ctx.fillRect(0, 0, CANVAS_SIZE, CANVAS_SIZE);

    // b. QR Code
    ctx.drawImage(qrImg, 0, 0, CANVAS_SIZE, CANVAS_SIZE);

    // c. Círculo branco por trás do coração
    ctx.fillStyle = "#FFFFFF";
    ctx.beginPath();
    ctx.arc(cx, cy, CIRCLE_R, 0, Math.PI * 2);
    ctx.fill();

    // d. Coração
    ctx.drawImage(heartImg, cx - HEART_PX / 2, cy - HEART_PX / 2, HEART_PX, HEART_PX);

    const link = document.createElement("a");
    link.download = filename;
    link.href = canvas.toDataURL("image/png");
    link.click();
  } catch {
    URL.revokeObjectURL(qrUrl);
    URL.revokeObjectURL(heartUrl);
  }
}

// ─── Props ────────────────────────────────────────────────────────────────────

export interface LovePageCodeProps {
  url: string;
  title?: string;
  subtitle?: string;
  coupleName?: string;
  templateSlug?: string;
  downloadable?: boolean;
  showQrCode?: boolean;
  className?: string;
}

// ─── Componente ───────────────────────────────────────────────────────────────

export function LovePageCode({
  url,
  title,
  coupleName,
  templateSlug,
  downloadable = true,
  showQrCode = true,
  className,
}: LovePageCodeProps) {
  const cfg = getTemplateConfig(templateSlug);
  const bars = generateBars(url);
  const qrRef = useRef<HTMLDivElement>(null);
  const [copied, setCopied] = useState(false);
  const [downloading, setDownloading] = useState(false);

  // Templates escuros usam fundo branco para garantir contraste no QR
  const qrFg = cfg.thumbnailDark ? cfg.accentHex : cfg.accentSecondary;
  const qrBg = cfg.thumbnailDark ? "#FFFFFF" : cfg.pageBackground;

  // Cor do coração: sempre vermelho ILovePage (#E11D48) para identidade visual consistente
  const heartColor = "#E11D48";

  const slug = url.split("/p/")[1]?.split("?")[0] ?? "lovepage";

  const copyLink = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(url);
    } catch {
      const el = document.createElement("textarea");
      el.value = url;
      document.body.appendChild(el);
      el.select();
      document.execCommand("copy");
      document.body.removeChild(el);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 2500);
  }, [url]);

  const handleDownload = useCallback(async () => {
    if (!qrRef.current) return;
    setDownloading(true);
    try {
      await downloadQrAsPng(qrRef.current, `lovepage-code-${slug}.png`, heartColor);
    } finally {
      setDownloading(false);
    }
  }, [slug, heartColor]);

  return (
    <div className={cn("flex flex-col gap-3", className)}>
      {/* ── Card principal ───────────────────────────────────────────────── */}
      <div className="rounded-2xl overflow-hidden shadow-xl border border-rose-100 bg-white">

        {/* Faixa superior com gradiente e barras decorativas */}
        <div
          className="relative px-4 py-3 flex items-center gap-3 overflow-hidden"
          style={{ background: cfg.headerGradient }}
        >
          <div className="shrink-0 z-10">
            <Heart className="h-5 w-5 fill-white text-white opacity-90" />
          </div>

          {/* Barras decorativas (ILovePage Code identity) */}
          <div
            className="flex-1 flex items-end gap-[2px] h-8 z-10"
            aria-hidden="true"
          >
            {bars.map((h, i) => (
              <div
                key={i}
                className="flex-1 rounded-full min-w-[2px]"
                style={{
                  height: `${h}%`,
                  background: "rgba(255,255,255,0.60)",
                  maxWidth: "4px",
                }}
              />
            ))}
          </div>

          <div className="shrink-0 z-10 text-right">
            <p className="text-[10px] font-black uppercase tracking-widest text-white opacity-90 leading-none">
              ILovePage
            </p>
            <p className="text-[10px] font-black uppercase tracking-widest text-white opacity-55 leading-none mt-[1px]">
              Code
            </p>
          </div>
        </div>

        {/* Corpo do card */}
        <div className={cn("flex flex-col items-center gap-4 px-5 py-6", cfg.bodyBgClass ?? "bg-white")}>

          {/* Título opcional */}
          {(title || coupleName) && (
            <div className="text-center">
              {title && (
                <p className={cn("text-sm font-bold leading-tight", cfg.bodyTextClass ?? "text-[#1F2937]")}>
                  {title}
                </p>
              )}
              {coupleName && (
                <p className={cn("text-xs mt-0.5 opacity-50", cfg.bodyTextClass ?? "text-[#1F2937]")}>
                  {coupleName}
                </p>
              )}
            </div>
          )}

          {/* QR Code com coração centralizado */}
          {showQrCode && (
            <div className="relative inline-flex" ref={qrRef}>
              {/* Quiet zone + fundo */}
              <div
                className="rounded-2xl p-4"
                style={{
                  background: qrBg,
                  boxShadow: `0 0 0 1.5px ${qrFg}18, 0 4px 16px 0 rgba(0,0,0,0.07)`,
                }}
              >
                <QRCode
                  value={url}
                  size={180}
                  fgColor={qrFg}
                  bgColor={qrBg}
                  level="H"
                  style={{ display: "block" }}
                />
              </div>

              {/* Coração vermelho centralizado — level H garante até 30% de recuperação */}
              <div
                className="absolute inset-0 flex items-center justify-center pointer-events-none"
                aria-hidden="true"
              >
                <div
                  className="rounded-full flex items-center justify-center"
                  style={{
                    width: 34,
                    height: 34,
                    background: "#FFFFFF",
                    boxShadow: `0 0 0 2.5px ${qrBg}`,
                  }}
                >
                  <Heart
                    className="fill-[#E11D48] text-[#E11D48]"
                    style={{ width: 18, height: 18 }}
                  />
                </div>
              </div>
            </div>
          )}

          {/* CTA */}
          <p className={cn("text-xs text-center font-semibold tracking-wide", cfg.accentClass ?? "text-[#E11D48]")}>
            Escaneie para abrir sua ILovePage
          </p>

          {/* URL */}
          <p
            className={cn(
              "text-[10px] text-center break-all max-w-[200px] leading-relaxed",
              cfg.bodyTextClass ?? "text-[#1F2937]",
              "opacity-35"
            )}
          >
            {url}
          </p>
        </div>
      </div>

      {/* ── Botões de ação ───────────────────────────────────────────────── */}
      <div className="grid grid-cols-2 gap-2">
        <Button
          variant="outline"
          size="sm"
          onClick={copyLink}
          aria-label="Copiar link da ILovePage"
          className="gap-2"
        >
          {copied ? (
            <Check className="h-3.5 w-3.5 text-green-500" />
          ) : (
            <Copy className="h-3.5 w-3.5" />
          )}
          {copied ? "Copiado!" : "Copiar link"}
        </Button>

        {downloadable && showQrCode && (
          <Button
            variant="outline"
            size="sm"
            onClick={handleDownload}
            disabled={downloading}
            aria-label="Baixar ILovePage Code como PNG"
            className="gap-2"
          >
            <Download className="h-3.5 w-3.5" />
            {downloading ? "Gerando..." : "Baixar PNG"}
          </Button>
        )}
      </div>
    </div>
  );
}
